import {
  BLOOD_GLUCOSE,
  BLOOD_PRESSURE,
  BODY_FAT_PERCENTAGE,
  BODY_MASS_INDEX,
  BODY_TEMPERATURE,
  PAIN,
  WAIST_HEIGHT_RATIO,
  WAIST_HIP_RATIO,
} from '@constants/fhir';

import type { getReferenceRangeMap } from '@utils/reference_range';

import { ParameterType, ReferenceRangeRiskLevel } from 'generated/types';

export type ReferenceRangeMap = ReturnType<typeof getReferenceRangeMap>;

export type FormValues = {
  parameters: ParameterValue[];
  submit: string | null;
};

export type ParameterValue = {
  /** ID of parameter */
  parameter_id: string;
  /** Code of parameter */
  code: string;
  /** Determines if parameter is default or custom */
  vitall_patient_id?: string | null;
  /** Lower limit of parameter */
  lower_bound?: number;
  /** Upper limit of parameter */
  upper_bound?: number;
  /** Between | Gt | Gte | Lt | Lte => determines (ex/in)clusivity */
  expression: ParameterExpression;
};

export enum ParameterExpression {
  Between = 'between',
  Lt = 'lt',
  Lte = 'lte',
  Gt = 'gt',
  Gte = 'gte',
}

export const ParameterExpressionMap = new Map<ParameterExpression, string>([
  [ParameterExpression.Between, 'insights.parameter.expression.between'],
  [ParameterExpression.Lt, 'insights.parameter.expression.lt'],
  [ParameterExpression.Lte, 'insights.parameter.expression.lte'],
  [ParameterExpression.Gt, 'insights.parameter.expression.gt'],
  [ParameterExpression.Gte, 'insights.parameter.expression.gte'],
]);

export const RISK_NAME_MAP = new Map([
  [
    ReferenceRangeRiskLevel.low,
    new Map([
      [BODY_MASS_INDEX, 'insights.riskLevel.low.underweight'],
      [ParameterType.default, 'insights.riskLevel.low.default'],
    ]),
  ],
  [
    ReferenceRangeRiskLevel.normal,
    new Map([
      [WAIST_HIP_RATIO, 'insights.riskLevel.normal.lowRisk'],
      [WAIST_HEIGHT_RATIO, 'insights.riskLevel.normal.lowRisk'],
      [ParameterType.default, 'insights.riskLevel.normal.default'],
    ]),
  ],
  [
    ReferenceRangeRiskLevel.mild,
    new Map([
      [BLOOD_PRESSURE, 'insights.riskLevel.mild.elevated'],
      [BODY_TEMPERATURE, 'insights.riskLevel.mild.high'],
      [BLOOD_GLUCOSE, 'insights.riskLevel.mild.borderline'],
      [BODY_MASS_INDEX, 'insights.riskLevel.mild.overweight'],
      [WAIST_HIP_RATIO, 'insights.riskLevel.mild.moderateRisk'],
      [WAIST_HEIGHT_RATIO, 'insights.riskLevel.mild.moderateRisk'],
      [ParameterType.default, 'insights.riskLevel.mild.default'],
    ]),
  ],
  [
    ReferenceRangeRiskLevel.moderate,
    new Map([
      [BLOOD_PRESSURE, 'insights.riskLevel.moderate.hypertension'],
      [BODY_MASS_INDEX, 'insights.riskLevel.moderate.obesity'],
      [ParameterType.default, 'insights.riskLevel.moderate.default'],
    ]),
  ],
  [
    ReferenceRangeRiskLevel.high,
    new Map([
      [BLOOD_PRESSURE, 'insights.riskLevel.high.hypertension'],
      [BODY_TEMPERATURE, 'insights.riskLevel.high.fever'],
      [PAIN, 'insights.riskLevel.high.severe'],
      [BLOOD_GLUCOSE, 'insights.riskLevel.high.danger'],
      [BODY_MASS_INDEX, 'insights.riskLevel.high.obesity'],
      [WAIST_HIP_RATIO, 'insights.riskLevel.high.highRisk'],
      [WAIST_HEIGHT_RATIO, 'insights.riskLevel.high.highRisk'],
      [BODY_FAT_PERCENTAGE, 'insights.riskLevel.high.veryHigh'],
      [ParameterType.default, 'insights.riskLevel.high.default'],
    ]),
  ],
  [
    ReferenceRangeRiskLevel.crisis,
    new Map([
      [BLOOD_PRESSURE, 'insights.riskLevel.crisis.hypertension'],
      [BODY_MASS_INDEX, 'insights.riskLevel.crisis.obesity'],
      [ParameterType.default, 'insights.riskLevel.crisis.default'],
    ]),
  ],
  [
    ReferenceRangeRiskLevel.below_target,
    new Map([
      [ParameterType.default, 'insights.riskLevel.belowTarget.default'],
    ]),
  ],
  [
    ReferenceRangeRiskLevel.target,
    new Map([[ParameterType.default, 'insights.riskLevel.target.default']]),
  ],
  [
    ReferenceRangeRiskLevel.ideal,
    new Map([[ParameterType.default, 'insights.riskLevel.ideal.default']]),
  ],
]);
